import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Users,
  GraduationCap,
  TrendingUp,
  Building2,
  Sparkles,
  Cloud,
  Calendar,
  Clock,
  BookOpen,
  Download,
  UserCheck,
  ChevronRight,
} from 'lucide-react';
import { Student, UserRole, Schedule } from '../types/db';

interface DashboardProps {
  students: Student[];
  branchName: string;
  role?: UserRole;
  userId?: string;
  schedules?: Schedule[];
}

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#F43F5E', '#8B5CF6', '#EC4899'];

export const Dashboard: React.FC<DashboardProps> = ({
  students,
  branchName,
  role,
  userId,
  schedules = [],
}) => {
  const isTentor = role === UserRole.TENTOR;

  const mySchedules = React.useMemo(() => {
    if (!isTentor || !userId) return [];
    // Filter jadwal khusus tentor yang sedang login
    return schedules.filter((s) => s.teacher_user_id === userId || s.teacher_id === userId);
  }, [schedules, isTentor, userId]);

  const activeStudents = React.useMemo(
    () => students.filter((s) => s.is_active !== false),
    [students]
  );

  const gradeDistribution = React.useMemo(() => {
    const counts: Record<string, number> = {};
    activeStudents.forEach((s) => {
      const level = s.grade?.split(' ')[0] || 'Unknown';
      counts[level] = (counts[level] || 0) + 1;
    });
    return Object.keys(counts).map((key) => ({ name: key, value: counts[key] }));
  }, [activeStudents]);

  const totalStudents = activeStudents.length;
  const inactiveStudentsCount = students.length - totalStudents;
  const activeClassesCount = new Set(activeStudents.map((s) => s.grade)).size;

  const subjectPopularity = React.useMemo(() => {
    const counts: Record<string, number> = {};
    activeStudents.forEach((s) => {
      if (s.subjects && Array.isArray(s.subjects)) {
        s.subjects.forEach((sub) => {
          const subjectName = sub.trim();
          counts[subjectName] = (counts[subjectName] || 0) + 1;
        });
      }
    });
    const data = Object.keys(counts).map((key) => ({ name: key, students: counts[key] }));
    if (data.length === 0) return [{ name: 'N/A', students: 0 }];
    return data.sort((a, b) => b.students - a.students).slice(0, 5);
  }, [activeStudents]);

  // --- RENDER TENTOR DASHBOARD ---
  if (isTentor) {
    return (
      <div className="space-y-6 animate-fade-in font-sans pb-10">
        <div className="bg-white p-8 md:p-10 rounded-[3rem] border border-slate-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-full -mr-16 -mt-16 opacity-50"></div>
          <div className="flex items-center gap-6 relative z-10">
            <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white shadow-xl shadow-indigo-100">
              <UserCheck className="w-10 h-10" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 uppercase tracking-tighter leading-none">
                Tentor Panel
              </h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2 flex items-center gap-2">
                <Building2 className="w-3 h-3 text-indigo-400" /> {branchName} • Command Center
              </p>
            </div>
          </div>
          <div className="flex gap-3 relative z-10">
            <div className="bg-emerald-50 px-5 py-3 rounded-2xl border border-emerald-100 text-center">
              <p className="text-[8px] font-black text-emerald-600 uppercase mb-1">Sesi Aktif</p>
              <p className="text-2xl font-black text-emerald-700">{mySchedules.length}</p>
            </div>
            <div className="bg-indigo-50 px-5 py-3 rounded-2xl border border-indigo-100 text-center">
              <p className="text-[8px] font-black text-indigo-600 uppercase mb-1">Status Sesi</p>
              <p className="text-2xl font-black text-indigo-700">LIVE</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white p-8 md:p-10 rounded-[3rem] border border-slate-100 shadow-sm">
            <div className="flex justify-between items-center mb-8">
              <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight flex items-center gap-3">
                <Calendar className="text-indigo-600 w-6 h-6" /> Jadwal Mengajar Anda
              </h3>
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
                Sync Live
              </span>
            </div>

            <div className="space-y-4">
              {mySchedules.length > 0 ? (
                mySchedules.map((s) => (
                  <div
                    key={s.id}
                    className="p-6 bg-slate-50 border border-slate-100 rounded-[2rem] flex flex-col md:flex-row justify-between items-center group hover:border-indigo-200 hover:bg-white transition-all"
                  >
                    <div className="flex items-center gap-6 w-full md:w-auto">
                      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-black text-slate-900 uppercase text-sm">{s.subject}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                          {s.day}, {s.time} WIB • {s.class_room?.name || s.grade}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4 md:mt-0 w-full md:w-auto">
                      {(s.tentor_material_url || s.teacher_material_url) && (
                        <a
                          href={s.tentor_material_url || s.teacher_material_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 md:flex-none px-5 py-2.5 bg-indigo-50 text-indigo-600 rounded-xl text-[9px] font-black uppercase flex items-center justify-center gap-2 border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all"
                        >
                          <Download className="w-3.5 h-3.5" /> Materi Pengajar
                        </a>
                      )}
                      <button className="flex-1 md:flex-none p-2.5 bg-slate-900 text-white rounded-xl hover:bg-indigo-600 transition-all shadow-lg">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-20 text-center border-4 border-dashed border-slate-50 rounded-[3rem]">
                  <Calendar className="w-12 h-12 text-slate-100 mx-auto mb-4" />
                  <p className="text-slate-300 font-black uppercase text-[10px] tracking-widest">
                    Belum ada jadwal mengajar yang terhubung.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-bl-full"></div>
              <Sparkles className="text-indigo-400 w-10 h-10 mb-6" />
              <h4 className="font-black text-xl uppercase tracking-tight mb-2">Pusat Akademik</h4>
              <p className="text-slate-400 text-xs font-medium leading-relaxed mb-8">
                Gunakan menu di samping untuk menginput{' '}
                <span className="text-white">Rapor Digital AI</span> atau membuat{' '}
                <span className="text-white">Ujian Baru</span> bagi siswa Anda.
              </p>
              <button className="w-full py-4 bg-indigo-600 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 shadow-xl shadow-indigo-900/50">
                <BookOpen className="w-4 h-4" /> E-MODULES ACCESS
              </button>
            </div>

            <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm text-center">
              <div className="w-14 h-14 bg-emerald-50 text-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Cloud className="w-8 h-8" />
              </div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                Status Kehadiran
              </p>
              <h5 className="font-black text-slate-900 uppercase">Terverifikasi Otomatis</h5>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDER ADMIN DASHBOARD (DEFAULT) ---
  return (
    <div className="space-y-6 animate-fade-in font-sans pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl md:text-2xl font-black text-slate-800 tracking-tighter uppercase leading-none">
              {branchName}
            </h2>
          </div>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-2">
            Sistem Manajemen Bimbel Terintegrasi
          </p>
        </div>
        <div className="flex gap-2">
          <div className="bg-emerald-50 px-4 py-2 rounded-2xl flex items-center gap-2 border border-emerald-100 w-fit">
            <Cloud className="w-4 h-4 text-emerald-500" />
            <p className="text-[9px] font-black text-emerald-700 uppercase tracking-widest">
              Cloud Database Connected
            </p>
          </div>
          <div className="bg-indigo-50 px-4 py-2 rounded-2xl flex items-center gap-2 border border-indigo-100 w-fit">
            <Sparkles className="w-4 h-4 text-indigo-500 animate-pulse" />
            <p className="text-[9px] font-black text-indigo-700 uppercase tracking-widest">
              AI Analysis Ready
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Siswa Aktif"
          value={totalStudents.toString()}
          label={
            inactiveStudentsCount > 0
              ? `${inactiveStudentsCount} Siswa Nonaktif`
              : 'Semua Siswa Aktif'
          }
          icon={<Users className="w-5 h-5" />}
          color="bg-indigo-600"
        />
        <StatCard
          title="Kelas Aktif"
          value={activeClassesCount.toString()}
          label="Tingkat Grade"
          icon={<GraduationCap className="w-5 h-5" />}
          color="bg-emerald-500"
        />
        <StatCard
          title="Absensi"
          value="94%"
          label="Rata-rata Kehadiran"
          icon={<TrendingUp className="w-5 h-5" />}
          color="bg-amber-500"
        />
        <StatCard
          title="Ujian AI"
          value="12"
          label="Bank Soal Aktif"
          icon={<Sparkles className="w-5 h-5" />}
          color="bg-rose-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 md:p-10 rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden group hover:shadow-md transition-shadow">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-8 flex items-center gap-2">
            <div className="w-1.5 h-4 bg-indigo-600 rounded-full"></div>
            Analisis Minat Belajar
          </h3>
          <div className="h-[250px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={subjectPopularity}
                margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  fontSize={9}
                  fontWeight={800}
                  tick={{ fill: '#94A3B8' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  fontSize={9}
                  fontWeight={800}
                  tick={{ fill: '#94A3B8' }}
                />
                <Tooltip
                  cursor={{ fill: '#F8FAFC' }}
                  contentStyle={{
                    borderRadius: '20px',
                    border: 'none',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  }}
                />
                <Bar dataKey="students" fill="#6366F1" radius={[8, 8, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 md:p-10 rounded-[2.5rem] shadow-sm border border-slate-100 group hover:shadow-md transition-shadow">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-8 text-center">
            Proporsi Siswa
          </h3>
          <div className="h-[220px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gradeDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={8}
                  dataKey="value"
                  stroke="none"
                >
                  {gradeDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: '20px',
                    border: 'none',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-8 space-y-2.5">
            {gradeDistribution.map((entry, index) => (
              <div
                key={entry.name}
                className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest px-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-md shadow-sm"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></span>
                  <span className="text-slate-500">{entry.name}</span>
                </div>
                <span className="text-slate-800 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">
                  {entry.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({
  title,
  value,
  label,
  icon,
  color,
}: {
  title: string;
  value: string;
  label?: string;
  icon: React.ReactNode;
  color: string;
}) => (
  <div className="bg-white p-5 md:p-6 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col justify-between group hover:border-indigo-200 hover:shadow-md transition-all relative overflow-hidden">
    <div className="flex justify-between items-start mb-4">
      <div
        className={`p-2.5 rounded-2xl ${color} text-white shadow-lg group-hover:scale-110 transition-transform duration-500`}
      >
        {icon}
      </div>
    </div>
    <div className="relative z-10">
      <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1">
        {title}
      </p>
      <h4 className="text-2xl font-black text-slate-800 tracking-tighter leading-none">{value}</h4>
      {label && (
        <p className="text-[7px] font-bold text-slate-300 uppercase tracking-widest mt-1.5">
          {label}
        </p>
      )}
    </div>
    <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-slate-50 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-700"></div>
  </div>
);
